/**
 * Monitoreo de los enlaces bancarios (Bind / STP / Coelsa).
 *
 * El backend deriva todo de la métrica cruda de CloudWatch, sin depender de que
 * existan alarmas:
 *   - GET /metrics/enlaces          → estado actual de los 4 túneles + 3 instancias
 *   - GET /metrics/enlaces/history  → uptime diario y eventos de caída
 *
 * Bind y STP son VPNs nativas de AWS con 2 túneles cada una (métrica AWS/VPN
 * TunnelState, 1=UP / 0=DOWN). Coelsa NO usa VPN nativa: termina el IPSec en un
 * MikroTik y AWS no publica métrica del túnel, así que su único "túnel" se infiere
 * de la latencia de la Lambda CoelsaConnectorProd (timeout ⇒ caído). La instancia
 * MikroTik se sigue mirando por StatusCheckFailed, pero eso solo detecta que la VM
 * murió: el 2026-09-04 el túnel estuvo 2,5 h caído con la VM sana.
 *
 * Contexto y decisiones: aws/intertik-access/05-plan-monitoreo-enlaces.md
 */
import { authFetch, authHeader, jsonOrThrow, qsFrom } from "./monitoring";

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  "http://localhost:3000";

// ---------- Tipos ----------
export type TunnelState = "up" | "down" | "unknown";
export type EnlaceState = "ok" | "degraded" | "down";
export type InstanceState = "ok" | "failed" | "unknown";

export interface Tunnel {
  n: number;
  ip: string;
  state: TunnelState;
  since: string | null;
  since_precision: "minute" | "day";
  since_capped: boolean;      // true → no hubo transición en toda la ventana
  lookback_days: number;
  uptime_6h: number | null;
  uptime_30d: number | null;
  uptime_lookback: number | null;
  days_full_up: number;
  days_any_up: number;        // 0 con datos ⇒ nunca estuvo arriba
  source?: string | null;     // null = métrica TunnelState; texto = serie inferida (Coelsa)
}

export interface Enlace {
  key: string;
  label: string;
  vpn_id: string;
  state: EnlaceState;
  tunnels_up: number;
  tunnels_total: number;
  single_tunnel?: boolean;    // true ⇒ "ok" es imposible: arriba = sin redundancia
  tunnels: Tunnel[];
}

export interface EnlaceInstance {
  key: string;
  label: string;
  instance_id: string;
  enlace: string;
  az: string;
  rol: string;
  state: InstanceState;
  failed_checks_6h: number;
  last_seen: string | null;
}

export interface EnlacesEstado {
  generated_at: string;
  lookback_days: number;
  enlaces: Enlace[];
  instances: EnlaceInstance[];
}

export interface OutageEvent {
  from: string;
  to: string;
  downtime_s: number;
  buckets: number;
}

export interface DayUptime { d: string; uptime: number }

export interface TunnelHistory {
  enlace: string;
  enlace_label: string;
  n: number;
  ip: string;
  resolution_s: number;       // puede ser más gruesa que la global (Coelsa: mínimo 5 min)
  uptime: number | null;
  days: DayUptime[];
  events: OutageEvent[];
}

export interface EnlacesHistory {
  generated_at: string;
  days: number;
  resolution_s: number;
  from: string;
  to: string;
  tunnels: TunnelHistory[];
  instances: { key: string; label: string; instance_id: string; enlace: string; events: OutageEvent[] }[];
}

// ---------- Llamadas ----------
export async function getEnlacesEstado(): Promise<EnlacesEstado> {
  const res = await authFetch(`${API_BASE}/metrics/enlaces`, { headers: authHeader() });
  return jsonOrThrow(res, "getEnlacesEstado");
}

export async function getEnlacesHistory(days: number): Promise<EnlacesHistory> {
  const url = `${API_BASE}/metrics/enlaces/history${qsFrom({ days: String(days) })}`;
  const res = await authFetch(url, { headers: authHeader() });
  return jsonOrThrow(res, "getEnlacesHistory");
}

// ---------- Formato ----------
export function fmtDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)} s`;
  const m = seconds / 60;
  if (m < 60) return `${Math.round(m)} min`;
  const h = m / 60;
  if (h < 48) return `${h.toFixed(1)} h`;
  return `${Math.round(h / 24)} días`;
}

/** Uptime como porcentaje. Distingue el 100% exacto del 99,99% redondeado. */
export function fmtUptime(v: number | null | undefined): string {
  if (v == null) return "—";
  if (v >= 1) return "100%";
  const pct = v * 100;
  if (pct > 99.99) return "99,99%";     // nunca mostrar "100%" si hubo downtime
  return `${pct.toFixed(2).replace(".", ",")}%`;
}

/** Resolución de la consulta → cómo describirla al usuario. */
export function fmtResolution(seconds: number): string {
  if (seconds <= 60) return "1 minuto";
  if (seconds <= 300) return "5 minutos";
  if (seconds <= 3600) return "1 hora";
  return "1 día";
}
